import React, { useEffect } from "react";
import { Row, Col, Button } from "react-bootstrap";
import InputField from "./InputField";
import { RiDeleteBin6Line } from "react-icons/ri";

const RelevantExperienceEntry = ({
  entry,
  outerIndex,
  relevantIndex,
  handleInputChange,
  deleteRelevantExperience,
  fieldErrors,
  style,
  checkErrorFn,
  parentStartDate,
  parentEndDate,
  setFieldErrors,
  calculateTotalExperience,
  validateExperienceConsistency,
  relaventTotalWithoutTraineeFun,
  allEntries,
  innerIndex,
  minExperienceDate,
  applicationDetails,
}) => {
  const isDateWithinRange = (date, minDate, maxDate) => {
    if (!date) return false;
    const dateValue = new Date(date);
    const min = minDate
      ? new Date(minDate)
      : new Date(minExperienceDate || "1900-01-01");
    const max = maxDate ? new Date(maxDate) : new Date();
    return dateValue >= min && dateValue <= max;
  };

  const relaventCalculationOnChange = (startDate, endDate) => {
    if (startDate && endDate) {
      // Validate against minimum experience date (highest qualification passing year)
      if (minExperienceDate && new Date(startDate) < new Date(minExperienceDate)) {
        const highestPassingYear = new Date(minExperienceDate).getFullYear();
        setFieldErrors((prevState) => ({
          ...prevState,
          [`startDateOfService${outerIndex}${relevantIndex}`]: {
            errors: [
              `Start date cannot be earlier than your highest qualification passing year (${highestPassingYear}). Please enter a date from ${highestPassingYear}-01-01 onwards.`,
            ],
            touched: true,
          },
        }));
        handleInputChange(
          outerIndex,
          null,
          "totalExperience",
          "",
          relevantIndex
        );
        return;
      }

      if (new Date(endDate) < new Date(startDate)) {
        setFieldErrors((prevState) => ({
          ...prevState,
          [`endDateOfServiceRelavent${outerIndex}${relevantIndex}`]: {
            errors: ["The 'To' date cannot be earlier than the 'From' date."],
            touched: true,
          },
        }));
        handleInputChange(
          outerIndex,
          null,
          "totalExperience",
          "",
          relevantIndex
        );
      } else {
        setFieldErrors((prevState) => ({
          ...prevState,
          [`endDateOfServiceRelavent${outerIndex}${relevantIndex}`]: {
            errors: [],
            touched: true,
          },
        }));
        const totalExperience = calculateTotalExperience(startDate, endDate);
        handleInputChange(
          outerIndex,
          null,
          "totalExperience",
          totalExperience,
          relevantIndex
        );
      }
    } else {
      handleInputChange(outerIndex, null, "totalExperience", "", relevantIndex);
    }
  };

  function getMinStartDateForEntry(entries, currentIndex, parentStartDate) {
    if (currentIndex === 0) return parentStartDate || minExperienceDate;
    const prevEndDate = entries?.[currentIndex - 1]?.endDateOfService;
    if (prevEndDate) {
      // Next day after previous end date
      const nextDay = new Date(prevEndDate);
      nextDay.setDate(nextDay.getDate() + 1);
      return nextDay.toISOString().split("T")[0];
    }
    return parentStartDate || minExperienceDate;
  }

  // Clamp child dates on parent change or sibling changes
  useEffect(() => {
    const computedMinStart = getMinStartDateForEntry(
      allEntries,
      innerIndex,
      parentStartDate
    );
    const computedMaxEnd =
      parentEndDate || new Date().toISOString().split("T")[0];

    let newStart = entry.startDateOfService;
    let newEnd = entry.endDateOfService;
    let changed = false;

    if (newStart) {
      if (computedMinStart && new Date(newStart) < new Date(computedMinStart)) {
        newStart = computedMinStart;
        changed = true;
      }
      if (computedMaxEnd && new Date(newStart) > new Date(computedMaxEnd)) {
        newStart = computedMaxEnd;
        changed = true;
      }
    }
    if (newEnd) {
      if (newStart && new Date(newEnd) < new Date(newStart)) {
        newEnd = newStart;
        changed = true;
      }
      if (computedMaxEnd && new Date(newEnd) > new Date(computedMaxEnd)) {
        newEnd = computedMaxEnd;
        changed = true;
      }
    }

    if (changed) {
      handleInputChange(
        outerIndex,
        null,
        "startDateOfService",
        newStart || "",
        relevantIndex
      );
      handleInputChange(
        outerIndex,
        null,
        "endDateOfService",
        newEnd || "",
        relevantIndex
      );
      relaventCalculationOnChange(newStart || "", newEnd || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentStartDate, parentEndDate, allEntries]);

  const localCheckErrorFn = (fieldKey, immediateValue) => {
    let errors = [];
    if (!immediateValue) {
      errors.push("This field is required.");
    } else if (
      fieldKey.includes("startDateOfServiceRelavent") ||
      fieldKey.includes("endDateOfServiceRelavent")
    ) {
      if (
        parentStartDate &&
        parentEndDate &&
        !isDateWithinRange(immediateValue, parentStartDate, parentEndDate)
      ) {
        errors.push(
          `Date must be between ${parentStartDate || minExperienceDate} and ${
            parentEndDate || new Date().toISOString().split("T")[0]
          }.`
        );
      }
      if (entry.startDateOfService && entry.endDateOfService) {
        if (
          new Date(entry.endDateOfService) < new Date(entry.startDateOfService)
        ) {
          errors.push("The 'To' date cannot be earlier than the 'From' date.");
        }
      }
    }
    setFieldErrors((prevState) => ({
      ...prevState,
      [fieldKey]: {
        errors: errors,
        touched: true,
      },
    }));
    return errors.length === 0;
  };

  const computedMinStart = getMinStartDateForEntry(
    allEntries,
    innerIndex,
    parentStartDate
  );

  return (
    <React.Fragment className="mt-2">
      <hr className="mt-3" />
      <Row>
        <Col xs={12} md={4}>
          <InputField
            label="Employment Category:"
            type="select"
            value={entry.isTrainee}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                null,
                "isTrainee",
                value,
                relevantIndex
              );
              checkErrorFn(
                `isTrainee${outerIndex}${relevantIndex}`,
                value,
                outerIndex,
                relevantIndex
              );
            }}
            onBlur={() =>
              checkErrorFn(
                `isTrainee${outerIndex}${relevantIndex}`,
                entry.isTrainee,
                outerIndex,
                relevantIndex
              )
            }
            error={
              fieldErrors?.[`isTrainee${outerIndex}${relevantIndex}`]?.errors
            }
            style={style}
            required
            options={[
              { value: "Yes", label: "Trainee" },
              {
                value: "No",
                label: "Other ( Regular / Contractual / Probationer )",
              },
            ]}
          />
        </Col>

        <Col xs={12} md={8}>
          <InputField
            label="Nature of Job Handled:"
            type="text"
            value={entry.relevantNatureOfJobHandled}
            max={500}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                null,
                "relevantNatureOfJobHandled",
                value,
                relevantIndex
              );
              checkErrorFn(
                `relevantNatureOfJobHandled${outerIndex}${relevantIndex}`,
                value,
                outerIndex,
                relevantIndex
              );
            }}
            onBlur={() =>
              checkErrorFn(
                `relevantNatureOfJobHandled${outerIndex}${relevantIndex}`,
                entry.relevantNatureOfJobHandled,
                outerIndex,
                relevantIndex
              )
            }
            error={
              fieldErrors?.[
                `relevantNatureOfJobHandled${outerIndex}${relevantIndex}`
              ]?.errors
            }
            style={style}
            required
          />
        </Col>
      </Row>

      {/* Geology Department Specific Fields - Displayed after Nature of Job Handled */}
      {applicationDetails?.vacancyId?.department?.name
        ?.trim()
        ?.toLowerCase() === "geology" && (
        <Row className="mt-2">
          <Col xs={12} md={6}>
            <InputField
              label="Name of the metalliferous mine:"
              value={entry.nameOfMetalliferousMine || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(
                  outerIndex,
                  null,
                  "nameOfMetalliferousMine",
                  value,
                  relevantIndex
                );
                checkErrorFn(
                  `nameOfMetalliferousMine${outerIndex}${relevantIndex}`,
                  value,
                  outerIndex,
                  relevantIndex
                );
              }}
              onBlur={() =>
                checkErrorFn(
                  `nameOfMetalliferousMine${outerIndex}${relevantIndex}`,
                  entry.nameOfMetalliferousMine,
                  outerIndex,
                  relevantIndex
                )
              }
              error={
                fieldErrors?.[
                  `nameOfMetalliferousMine${outerIndex}${relevantIndex}`
                ]?.errors
              }
              style={style}
              required
            />
          </Col>
          <Col xs={12} md={6}>
            <InputField
              label="Location:"
              value={entry.mineLocation || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(
                  outerIndex,
                  null,
                  "mineLocation",
                  value,
                  relevantIndex
                );
                checkErrorFn(
                  `mineLocation${outerIndex}${relevantIndex}`,
                  value,
                  outerIndex,
                  relevantIndex
                );
              }}
              onBlur={() =>
                checkErrorFn(
                  `mineLocation${outerIndex}${relevantIndex}`,
                  entry.mineLocation,
                  outerIndex,
                  relevantIndex
                )
              }
              error={
                fieldErrors?.[`mineLocation${outerIndex}${relevantIndex}`]
                  ?.errors
              }
              style={style}
              required
            />
          </Col>
        </Row>
      )}

      {/* Sales & Marketing Department Specific Fields - Displayed after Nature of Job Handled */}
      {applicationDetails?.vacancyId?.department?.name
        ?.trim()
        ?.toLowerCase() === "sales & marketing" && (
        <Row className="mt-2">
          <Col xs={12} md={6}>
            <InputField
              label="Sales & Marketing experience:"
              type="select"
              value={entry.salesMarketingExperience || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(
                  outerIndex,
                  null,
                  "salesMarketingExperience",
                  value,
                  relevantIndex
                );
                // Clear related finished product if not "related finished products"
                if (value !== "Related finished products") {
                  handleInputChange(
                    outerIndex,
                    null,
                    "relatedFinishedProduct",
                    "",
                    relevantIndex
                  );
                }
                checkErrorFn(
                  `salesMarketingExperience${outerIndex}${relevantIndex}`,
                  value,
                  outerIndex,
                  relevantIndex
                );
              }}
              onBlur={() =>
                checkErrorFn(
                  `salesMarketingExperience${outerIndex}${relevantIndex}`,
                  entry.salesMarketingExperience,
                  outerIndex,
                  relevantIndex
                )
              }
              error={
                fieldErrors?.[
                  `salesMarketingExperience${outerIndex}${relevantIndex}`
                ]?.errors
              }
              style={style}
              required
              options={[
                { value: "Iron Ore", label: "Iron Ore" },
                { value: "Iron Pellets", label: "Iron Pellets" },
                { value: "Chrome Ore", label: "Chrome Ore" },
                { value: "Bauxite Ore", label: "Bauxite Ore" },
                { value: "Manganese Ore", label: "Manganese Ore" },
                { value: "Limestone Ore", label: "Limestone Ore" },
                { value: "Bulk Raw materials", label: "Bulk Raw materials" },
                {
                  value: "Related finished products",
                  label: "Related finished products",
                },
              ]}
            />
          </Col>
          {entry.salesMarketingExperience === "Related finished products" && (
            <Col xs={12} md={6}>
              <InputField
                label="Please specify the related finished product:"
                value={entry.relatedFinishedProduct || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange(
                    outerIndex,
                    null,
                    "relatedFinishedProduct",
                    value,
                    relevantIndex
                  );
                  checkErrorFn(
                    `relatedFinishedProduct${outerIndex}${relevantIndex}`,
                    value,
                    outerIndex,
                    relevantIndex
                  );
                }}
                onBlur={() =>
                  checkErrorFn(
                    `relatedFinishedProduct${outerIndex}${relevantIndex}`,
                    entry.relatedFinishedProduct,
                    outerIndex,
                    relevantIndex
                  )
                }
                error={
                  fieldErrors?.[
                    `relatedFinishedProduct${outerIndex}${relevantIndex}`
                  ]?.errors
                }
                style={style}
                required
              />
            </Col>
          )}
        </Row>
      )}

      <Row>
        <Col xs={12} md={4}>
          <InputField
            label="From:"
            type="date"
            value={entry.startDateOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                null,
                "startDateOfService",
                value,
                relevantIndex
              );

              relaventCalculationOnChange(value, entry.endDateOfService);
              localCheckErrorFn(
                `startDateOfService${outerIndex}${relevantIndex}`,
                value
              );
            }}
            onBlur={() =>
              localCheckErrorFn(
                `startDateOfService${outerIndex}${relevantIndex}`,
                entry.startDateOfService
              )
            }
            error={
              fieldErrors?.[`startDateOfService${outerIndex}${relevantIndex}`]
                ?.errors
            }
            style={style}
            required
            min={computedMinStart}
            max={parentEndDate || new Date().toISOString().split("T")[0]}
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label="To:"
            type="date"
            value={entry.endDateOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(
                outerIndex,
                null,
                "endDateOfService",
                value,
                relevantIndex
              );
              relaventCalculationOnChange(entry.startDateOfService, value);
              localCheckErrorFn(
                `endDateOfServiceRelavent${outerIndex}${relevantIndex}`,
                value
              );
            }}
            onBlur={() =>
              localCheckErrorFn(
                `endDateOfServiceRelavent${outerIndex}${relevantIndex}`,
                entry.endDateOfService
              )
            }
            error={
              fieldErrors?.[
                `endDateOfServiceRelavent${outerIndex}${relevantIndex}`
              ]?.errors
            }
            style={style}
            required
            min={entry.startDateOfService || computedMinStart}
            max={parentEndDate || new Date().toISOString().split("T")[0]}
          />
        </Col>

        <Col xs={10} md={3}>
          <InputField
            label="Total Experience:"
            value={entry.totalExperience}
            onChange={() => {
              // Read-only field; no action needed
            }}
            style={style}
            required
            readOnly={true}
          />
        </Col>
        <Col xs={1} className="mt-4 text-end">
          <Button
            variant="foreground-alternate"
            size="lg"
            className="mt-0 pt-0"
            onClick={() => deleteRelevantExperience(outerIndex, relevantIndex)}
          >
            <RiDeleteBin6Line />
          </Button>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default RelevantExperienceEntry;
